import { useQuery } from "@tanstack/react-query";
import { unifiedSports } from "@/lib/sports/unifiedSportsService";
import type { ProviderMatchEvent } from "@/lib/sports/providers/types";

export interface UseMatchEventsResult {
  events: ProviderMatchEvent[];
  isLoading: boolean;
  isError: boolean;
  updatedAt: number | undefined;
}

/**
 * Real Data Phase 2 — official match events (API-Football `/fixtures/events`).
 *
 * Never fabricates. On provider failure returns [] so the renderer can show
 * the lifecycle-appropriate placeholder from `dataPolicy.ts`.
 */
export function useMatchEvents(fixtureId: string | undefined): UseMatchEventsResult {
  const enabled = typeof fixtureId === "string" && fixtureId.length > 0;
  const queryKey = ["match-events", fixtureId ?? "__none__"] as const;
  if (typeof window !== "undefined") {
    console.info("[TRACE][useMatchEvents][mount]", { enabled, fixtureId, queryKey });
  }
  const q = useQuery<ProviderMatchEvent[]>({
    queryKey,
    queryFn: async () => {
      const t0 = Date.now();
      console.info("[TRACE][useMatchEvents][queryFn:start]", { fixtureId, at: new Date().toISOString() });
      try {
        const r = await unifiedSports.getEvents(fixtureId as string);
        console.info("[TRACE][useMatchEvents][queryFn:success]", { fixtureId, ms: Date.now() - t0, count: r?.length ?? 0 });
        return r;
      } catch (e) {
        console.warn("[TRACE][useMatchEvents][queryFn:error]", { fixtureId, err: String(e) });
        throw e;
      }
    },
    enabled,
    refetchInterval: 30_000,
    staleTime: 20_000,
    retry: 1,
  });
  if (typeof window !== "undefined") {
    console.info("[TRACE][useMatchEvents][state]", {
      fixtureId, enabled, status: q.status, fetchStatus: q.fetchStatus,
      dataUpdatedAt: q.dataUpdatedAt, isFetching: q.isFetching, count: q.data?.length ?? 0,
    });
  }
  return {
    events: q.data ?? [],
    isLoading: enabled && q.isLoading,
    isError: q.isError,
    updatedAt: q.dataUpdatedAt || undefined,
  };
}